/**
 * Utility functions for mapping INSEE codes to human-readable labels
 * Based on official INSEE documentation: https://www.insee.fr/fr/information/2028129
 */

/**
 * Maps staff size codes to their corresponding labels
 * @param code The INSEE staff size code
 * @returns The human-readable label for the code
 */
export const getStaffSizeLabel = (code: string | undefined): string => {
  if (!code) return 'Non disponible';
  
  const staffSizeMap: Record<string, string> = {
    'NN': 'Non renseigné',
    '00': '0 salarié',
    '01': '1 ou 2 salariés',
    '02': '3 à 5 salariés',
    '03': '6 à 9 salariés',
    '11': '10 à 19 salariés',
    '12': '20 à 49 salariés',
    '21': '50 à 99 salariés',
    '22': '100 à 199 salariés',
    '31': '200 à 249 salariés',
    '32': '250 à 499 salariés',
    '41': '500 à 999 salariés',
    '42': '1 000 à 1 999 salariés',
    '51': '2 000 à 4 999 salariés',
    '52': '5 000 à 9 999 salariés',
    '53': '10 000 salariés et plus',
  };
  
  return staffSizeMap[code] || `Code: ${code}`;
};

/**
 * Maps administrative status codes to their corresponding labels
 * @param code The INSEE administrative status code
 * @returns The human-readable label for the code
 */
export const getAdminStatusLabel = (code: string | undefined): string => {
  if (!code) return 'Non disponible';
  
  const adminStatusMap: Record<string, string> = {
    'A': 'Actif',
    'F': 'Fermé',
    'C': 'Cessé',
  };
  
  return adminStatusMap[code] || `Code: ${code}`;
};

/**
 * Maps ESS (Économie Sociale et Solidaire) codes to their corresponding labels
 * @param code The INSEE ESS code
 * @returns The human-readable label for the code
 */
export const getESSLabel = (code: string | undefined): string => {
  if (!code) return 'Non disponible';
  
  const essMap: Record<string, string> = {
    'O': 'Oui',
    'N': 'Non',
  };
  
  return essMap[code] || `Code: ${code}`;
};

/**
 * Formats a date string to a French locale format
 * @param dateString The date string to format (YYYY-MM-DD or YYYYMMDD)
 * @returns The formatted date string
 */
export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Non disponible';
  
  // Check if the date is in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }
  
  // If it's in YYYYMMDD format (common in INSEE data)
  if (/^\d{8}$/.test(dateString)) {
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    return `${day}/${month}/${year}`;
  }
  
  return dateString;
}; 